import { type ReactElement, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { useEntityDetailPageScaffold } from '@/features/shared/hooks/useEntityDetailPageScaffold';
import { useTestDetailPageViewModel } from '@/features/tests/hooks/useTestDetailPageViewModel';
import { useProjectContextGuard } from '@/shared/hooks/useProjectContextGuard';
import { DetailPageScaffold } from '@/ui/composites/scaffolds';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { useDeleteTest, useTestDetailQuery } from '../hooks/tests';
import { TestDetailContent } from './TestDetailContent';
import type { AuthSession } from '@/auth/types';

type TestDetailPageProps = { session: AuthSession | null };

export const TestDetailPage = ({ session }: TestDetailPageProps): ReactElement => {
  const { slug } = useParams<{ slug: string }>();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const deleteTest = useDeleteTest();
  const { setOverrideItems, clearOverride } = useBreadcrumbs();
  const searchSlug = useMemo(() => (slug ?? '').trim(), [slug]);
  const lastBreadcrumbKey = useRef<string | null>(null);

  const testQuery = useTestDetailQuery(searchSlug || null, selectedProjectId ?? null);
  const { data: test, isPending: isTestPending, isError: isTestError, error } = testQuery;

  useProjectContextGuard({
    entity: test,
    isPending: isTestPending,
    isFetched: testQuery.isFetched,
    selectedProjectId,
    setSelectedProjectId,
    syncContextOnMismatch: true,
    listPath: '/tests',
  });
  const detailPage = useEntityDetailPageScaffold({
    load: {
      data: test,
      isPending: isTestPending,
      isFetched: testQuery.isFetched,
    },
    delete: {
      mutation: deleteTest,
      successMessage: 'Test deleted.',
      fallbackErrorMessage: 'Failed to delete test.',
    },
    redirects: {
      onDeleteTo: '/tests',
      notFoundBackLabel: 'Back to tests',
    },
    notFoundCopy: {
      title: 'Test not found',
      body: 'This test does not exist or you no longer have access to it.',
    },
  });
  const pageViewModel = useTestDetailPageViewModel({ test });

  useEffect(() => {
    if (!test) return () => undefined;
    const nextKey = `${test.id}:${test.name}`;
    if (lastBreadcrumbKey.current === nextKey) return () => undefined;
    lastBreadcrumbKey.current = nextKey;
    setOverrideItems([{ label: 'Tests', href: '/tests' }, { label: test.name }]);
    return () => {
      clearOverride();
      lastBreadcrumbKey.current = null;
    };
  }, [clearOverride, setOverrideItems, test]);

  if (detailPage.showNotFound) return detailPage.renderNotFound();

  return (
    <DetailPageScaffold
      session={session}
      sessionMessage="Sign in to view test details."
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      isPending={isTestPending}
      isError={isTestError}
      error={error}
      errorMessage="Failed to load test"
      data={test}
      loadingFallback={<></>}
    >
      {(resolvedTest) => (
        <TestDetailContent
          test={resolvedTest}
          viewModel={pageViewModel}
          onDelete={detailPage.handleDelete}
          deleteLoading={detailPage.deleteLoading}
        />
      )}
    </DetailPageScaffold>
  );
};
